'use client';

import { useEffect, useState, useRef } from 'react';

/**
 * `useNowSec(intervalMs)` — a low-frequency clock for time-based UI
 * decisions that don't need per-frame precision.
 *
 * Backed by a single module-level scheduler so N consumers at the same
 * cadence share one timer. The previous implementation gave every
 * caller its own `setInterval`, which meant the lobby board canvas
 * fired up to six independent 1 Hz timers per visit (price overlay,
 * forfeit overlay, avatar overlay, dutch chip, claim drawer, buyout
 * drawer). That was both wasteful and made the per-tab work scale
 * with how many surfaces the user opened; the shared scheduler caps
 * the cost at "one timer per distinct cadence" regardless of mount
 * count. Same `Math.floor(Date.now() / 1000)` unit and same
 * default-60s contract — drop-in replacement.
 *
 * Pause behaviour:
 *  - the shared scheduler stops ticking while the page is hidden
 *    (`document.visibilityState === 'hidden'`) and snaps + resumes
 *    on visibility change. This is the dominant win on mobile, where
 *    every backgrounded second of a hidden tab still drove a React
 *    commit on every overlay before.
 *  - opt-in `enabled` lets a consumer (e.g. an off-screen tile via
 *    `IntersectionObserver`) suppress its participation entirely.
 *    Disabled consumers still receive the latest published `nowSec`
 *    on mount but don't re-render until they re-enable.
 */
export function useNowSec(intervalMs: number = 60_000, enabled: boolean = true): number {
  const [nowSec, setNowSec] = useState(() => Math.floor(Date.now() / 1000));

  // Track the latest `enabled` flag without re-subscribing every render
  // (which would defeat the shared-scheduler dedupe by churning
  // subscribers each commit).
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  useEffect(() => {
    setNowSec(Math.floor(Date.now() / 1000));
    if (!enabled) return;

    const cadenceMs = Math.max(1_000, intervalMs);
    const unsubscribe = subscribeNowSec(cadenceMs, (next) => {
      // Re-check enabled at delivery time so a hook that flipped off
      // between schedule + tick doesn't trigger a wasted render.
      if (!enabledRef.current) return;
      setNowSec(next);
    });
    return unsubscribe;
  }, [intervalMs, enabled]);

  return nowSec;
}

// =============================================================================
// Shared scheduler — one timer per distinct cadence, paused while hidden.
// =============================================================================

type Subscriber = (nowSec: number) => void;
interface CadenceBucket {
  /** Active `setInterval` id (null while paused or no subscribers). */
  timer: ReturnType<typeof setInterval> | null;
  /** Last `nowSec` we published (snapshotted on visibility-resume). */
  lastTick: number;
  subscribers: Set<Subscriber>;
}

const buckets = new Map<number, CadenceBucket>();
let visibilityWired = false;

function getBucket(cadenceMs: number): CadenceBucket {
  let bucket = buckets.get(cadenceMs);
  if (!bucket) {
    bucket = {
      timer: null,
      lastTick: Math.floor(Date.now() / 1000),
      subscribers: new Set(),
    };
    buckets.set(cadenceMs, bucket);
  }
  return bucket;
}

function startBucket(cadenceMs: number): void {
  if (typeof window === 'undefined') return;
  const bucket = getBucket(cadenceMs);
  if (bucket.timer !== null) return;
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
  bucket.timer = setInterval(() => {
    bucket.lastTick = Math.floor(Date.now() / 1000);
    for (const fn of bucket.subscribers) fn(bucket.lastTick);
  }, cadenceMs);
}

function stopBucket(bucket: CadenceBucket): void {
  if (bucket.timer !== null) {
    clearInterval(bucket.timer);
    bucket.timer = null;
  }
}

function wireVisibility(): void {
  if (visibilityWired) return;
  if (typeof document === 'undefined') return;
  visibilityWired = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      // Pause every cadence's timer — backgrounded tabs shouldn't pay
      // for clock ticks that no human is observing.
      for (const bucket of buckets.values()) stopBucket(bucket);
      return;
    }
    // On resume, snap each bucket's `lastTick` and notify subscribers
    // once so any clock-dependent UI catches up before the next
    // scheduled tick lands.
    const nowSec = Math.floor(Date.now() / 1000);
    for (const [cadenceMs, bucket] of buckets.entries()) {
      if (bucket.subscribers.size === 0) continue;
      bucket.lastTick = nowSec;
      for (const fn of bucket.subscribers) fn(nowSec);
      startBucket(cadenceMs);
    }
  });
}

function subscribeNowSec(cadenceMs: number, fn: Subscriber): () => void {
  wireVisibility();
  const bucket = getBucket(cadenceMs);
  bucket.subscribers.add(fn);
  startBucket(cadenceMs);
  // Push the most-recent value synchronously so the consumer's first
  // render observes the shared bucket's clock instead of waiting on
  // the first interval fire.
  fn(bucket.lastTick);
  return () => {
    bucket.subscribers.delete(fn);
    if (bucket.subscribers.size === 0) {
      stopBucket(bucket);
      // Keep the bucket entry in the map so a subsequent subscribe
      // hits the cached `lastTick` without resetting the snap.
    }
  };
}
